import { useEffect, useRef } from 'react';
import type { BuildRequestResult } from '@/api/schemas';
import { defaultBranch, mergeAutoSubmit, mergeLaunchDefaults } from './runBuildModel';
import type { RunBuildFields } from './useRunBuildFields';
import type { RunBuildTarget } from './useRunBuildTarget';

/** Defaults follow the data: every available platform on, the environment's first allowed branch, launch-form defaults. */
export function useRunBuildDefaults(t: RunBuildTarget, f: RunBuildFields) {
  const { availablePlatforms, env, launchData } = t;
  const { platforms, branch } = f.values;
  const { setPlatforms, setBranch, setInputs, setAutoSubmit } = f.set;
  useEffect(() => {
    if (availablePlatforms.length > 0 && platforms.length === 0) setPlatforms(availablePlatforms);
  }, [availablePlatforms, platforms.length]);
  useEffect(() => {
    if (env && !branch) setBranch(defaultBranch(env));
  }, [env, branch]);
  useEffect(() => {
    if (!launchData) return;
    setInputs((prev) => mergeLaunchDefaults(prev, launchData.defaults));
    setAutoSubmit((prev) => mergeAutoSubmit(prev, launchData.defaults));
  }, [launchData]);
}

/** A different target invalidates the preview. */
export function useClearPreviewOnChange(t: RunBuildTarget, f: RunBuildFields, setPreview: (p: BuildRequestResult | null) => void) {
  const { app, group, env, selectedPlatforms } = t;
  const { branch, commitSha, inputs, priority, autoSubmit } = f.values;
  const selectedKey = selectedPlatforms.join(',');
  useEffect(() => setPreview(null), [app?.id, group?.slug, env?.slug, selectedKey, branch, commitSha, inputs, priority, autoSubmit]);
}

/** Radix focuses the first tabbable while the form is still a skeleton (the footer): move focus to the first field once it exists. */
export function useFocusFirstField(loadingBasics: boolean) {
  const focusedOnce = useRef(false);
  useEffect(() => {
    if (loadingBasics || focusedOnce.current) return;
    focusedOnce.current = true;
    document.getElementById('run-build-app')?.focus({ preventScroll: true });
  });
}
